import { HardwareService } from '../../../HardwareService';
import { barcodeDetectorProbe } from './detector';
import type { Pdf417Decoder } from './types';
import { zxingWasmProbe } from './wasm';

/**
 * `zxing-wasm` first, the platform API as the fallback. `BarcodeDetector` can advertise `pdf417`
 * through `getSupportedFormats()` and then read nothing, which is indistinguishable from an
 * unreadable card; `zxing-wasm` is the rung with real-image coverage, so it leads. On iOS Safari
 * there is no platform detector at all, so the wasm is the only rung.
 */
const probes = [zxingWasmProbe, barcodeDetectorProbe];

/**
 * Only the preferred decoder is ever held.
 *
 * Memoising a fallback turned one transient wasm failure into a permanent one: `BarcodeDetector` is
 * not null, so it was kept for the rest of the page's life and every later read decoded nothing.
 * Leaving it unheld means the next call probes the wasm again, which is the cheap version of the
 * same guarantee — a still-image read resolves once and then decodes, so it has no long-lived
 * session to promote a better rung into. That belongs with the camera, not here.
 */
let held: Pdf417Decoder | null = null;

/** One shared attempt, so overlapping callers cannot each start their own ~931 KB fetch. */
let inFlight: Promise<Pdf417Decoder | null> | null = null;

const probe = async (): Promise<Pdf417Decoder | null> => {
  for (const candidate of probes) {
    const decoder = await candidate.create();
    if (decoder) {
      if (candidate === zxingWasmProbe) {
        held = decoder;
      }
      return decoder;
    }
  }
  return null;
};

/** Never caches the absence of a decoder, and never caches a fallback. */
export const resolvePdf417Decoder = (): Promise<Pdf417Decoder | null> => {
  if (held) {
    return Promise.resolve(held);
  }

  if (inFlight) {
    return inFlight;
  }

  const attempt = probe();
  inFlight = attempt;
  const settled = attempt.finally(() => {
    if (inFlight === attempt) {
      inFlight = null;
    }
  });
  // The caller receives `settled`, so nothing is left to turn a rejection into an unhandled one.
  return settled;
};

export const resetPdf417Decoder = (): void => {
  held = null;
  inFlight = null;
};

/** `gaveUp` means resolution had not answered inside the budget; a null decoder means there is none. */
export type DecoderResolution = {
  decoder: Pdf417Decoder | null;
  gaveUp: boolean;
};

/**
 * Resolving the decoder is the one step with no timeout of its own: the first call fetches the
 * ~931 KB wasm and overlapping callers share that in-flight promise, so an upload on a stalled
 * network never settled and the operator was never offered hand entry — the one thing that still
 * works there. The budget and the cancellation signal live here so every caller inherits both.
 */
export const resolvePdf417DecoderWithin = async (
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<DecoderResolution> => {
  if (signal?.aborted) {
    return { decoder: null, gaveUp: true };
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort: (() => void) | undefined;

  try {
    const gaveUp = new Promise<'gave-up'>((resolve) => {
      timer = setTimeout(() => resolve('gave-up'), Math.max(0, timeoutMs));
      onAbort = () => resolve('gave-up');
      signal?.addEventListener('abort', onAbort, { once: true });
    });

    // A rejecting probe is part of the contract — a cold wasm load on a blocked network rejects, and
    // the absence is deliberately never cached. The caller cannot use a thrown error: it would
    // escape before any terminal state was reported, so it is answered as having no decoder.
    const resolving = resolvePdf417Decoder().catch((error: unknown) => {
      HardwareService.logger.e('PDF417 decoder resolution failed', { error });
      return null;
    });

    const winner = await Promise.race([resolving, gaveUp]);

    return winner === 'gave-up' ? { decoder: null, gaveUp: true } : { decoder: winner, gaveUp: false };
  } finally {
    // The loser outlives the race, so its timer and listener are cleared either way.
    clearTimeout(timer);
    if (onAbort) {
      signal?.removeEventListener('abort', onAbort);
    }
  }
};
